import React, { useMemo } from 'react';
import { Trophy } from 'lucide-react';
import { useTXCup } from '../contexts/TXCupContext';

interface TXCupStandingsProps {
  searchTerm: string;
}

export function TXCupStandings({ searchTerm }: TXCupStandingsProps) {
  const { standings, isLoading, error } = useTXCup();

  // Filter standings based on search term
  const filteredStandings = useMemo(() => {
    if (!searchTerm) return standings;
    
    return standings.filter(standing => 
      standing.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [standings, searchTerm]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="text-center mt-4 text-gray-600">Loading standings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-red-600">
          <p>Error loading standings: {error}</p>
        </div>
      </div>
    );
  }

  // Count members with valid scores
  const rankedMembers = filteredStandings.filter(s => s.totalScore !== null).length;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {filteredStandings.length === 0 ? (
        <div className="text-center py-8">
          <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Standings Available
          </h3>
          <p className="text-gray-600 max-w-sm mx-auto">
            Players need at least 2 tournament scores in the 2025 season to be ranked.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  Pos
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Player
                </th>
                <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 mt-2">
              {filteredStandings.map((standing) => (
                <tr 
                  key={standing.name} 
                  className={`hover:bg-gray-50 ${
                    standing.position && standing.position <= 30 
                      ? 'bg-green-50 hover:bg-green-100' 
                      : ''
                  }`}
                >
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500">
                    {standing.position || '-'}
                  </td>
                  <td className={`px-2 py-2 whitespace-nowrap text-sm font-medium ${
                    standing.position && standing.position <= 30 
                      ? 'text-green-900' 
                      : 'text-gray-900'
                  }`}>
                    {standing.name}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap text-sm text-right">
                    {standing.totalScore !== null ? (
                      <span className={
                        standing.position && standing.position <= 30 
                          ? 'text-green-900 font-medium' 
                          : 'text-gray-900'
                      }>{standing.totalScore}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}